import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { api, getActiveClubName, getActiveClubLogo } from "../lib/api";
import { toast } from "sonner";

export default function MatchdayReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.matchdayReport(id).then(setData); }, [id]);

  if (!data) return <div className="font-display text-2xl p-8">Lade Spielbericht...</div>;
  const { season, matchday, matches, players, table } = data;
  const completed = matches.filter((match) => match.played).length;
  const clubName = getActiveClubName();
  const clubLogo = getActiveClubLogo();
  const sendByEmail = async () => {
    setSending(true);
    try {
      const result = await api.emailMatchdayReport(id);
      toast.success(`PDF wurde an ${result.recipient} gesendet`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "E-Mail konnte nicht verschickt werden");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <style>{`
        @page { size: A4; margin: 7mm; }
        @media print {
          html, body, #root, .App, .App > *, .App > * > * { height: auto !important; min-height: 0 !important; }
          body { margin: 0 !important; background: white !important; color: #111 !important; }
          body::before, header, footer, .no-print { display: none !important; }
          main { max-width: none !important; padding: 0 !important; margin: 0 !important; min-height: 0 !important; }
          .matchday-report { max-width: 100% !important; color: #111 !important; font-size: 9px !important; line-height: 1.1 !important; }
          .matchday-report * { color: #111 !important; border-color: #999 !important; }
          .matchday-report .print-muted { color: #555 !important; }
          .matchday-report .print-title { color: #c62828 !important; }
          .matchday-report .print-shade { background: #f3f3f3 !important; }
          .matchday-report > * + * { margin-top: 7px !important; }
          .matchday-report .report-head { padding: 9px !important; }
          .matchday-report .report-head .club-logo { width: 44px !important; height: 44px !important; }
          .matchday-report .report-head .club-brand { font-size: 22px !important; line-height: 1 !important; }
          .matchday-report .report-head h1 { font-size: 28px !important; line-height: 1 !important; margin-top: 2px !important; }
          .matchday-report .report-section { padding: 9px !important; }
          .matchday-report .report-section h2 { font-size: 20px !important; line-height: 1 !important; }
          .matchday-report .report-section p { margin: 3px 0 5px !important; }
          .matchday-report .report-section th { padding-top: 3px !important; padding-bottom: 3px !important; }
          .matchday-report .report-section td { padding-top: 3px !important; padding-bottom: 3px !important; }
        }
      `}</style>
      <main className="matchday-report max-w-4xl mx-auto space-y-6" data-testid="matchday-report">
        <div className="flex justify-between items-center no-print">
          <Link to="/spielplan" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }}><ArrowLeft size={14} /> Zurück</Link>
          <div className="flex gap-2 flex-wrap justify-end">
            <button className="dc-btn dc-btn-ghost" disabled={sending} onClick={sendByEmail} data-testid="matchday-report-email">{sending ? "Versende..." : "PDF per E-Mail senden"}</button>
            <button className="dc-btn" onClick={() => window.print()} data-testid="matchday-report-print"><Printer size={14} /> Als PDF speichern / drucken</button>
          </div>
        </div>

        <section className="dc-border p-7 print-shade report-head">
          <div className="relative flex justify-center text-center">
            {clubLogo && <img src={clubLogo} alt={`${clubName} Logo`} className="club-logo absolute left-0 top-1/2 -translate-y-1/2 w-20 h-20 object-contain dc-border p-1" style={{ background: "#fff" }} />}
            <div>
          <div className="club-brand font-display text-4xl uppercase tracking-wide print-title" style={{ color: "var(--dc-primary)" }}>{clubName}</div>
          <h1 className="font-display text-4xl mt-2">Spielbericht - Spieltag {matchday.number}</h1>
          <div className="mt-2 text-sm print-muted" style={{ color: "var(--dc-muted)" }}>{season?.name || "Saison"}{matchday.date ? ` · ${matchday.date}` : ""} · {completed} von {matches.length} Partien gespielt</div>
            </div>
          </div>
        </section>

        <section className="dc-border p-6 report-section">
          <h2 className="font-display text-2xl mb-1">Tabelle des Spieltags</h2>
          <p className="text-sm mb-4 print-muted" style={{ color: "var(--dc-muted)" }}>Alle am Spieltag anwesenden Spieler, einschließlich Gäste.</p>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase print-muted" style={{ color: "var(--dc-muted)" }}>
              <tr><th className="text-left py-2">Pl.</th><th className="text-left">Spieler</th><th className="text-right">Sp.</th><th className="text-right">S</th><th className="text-right">N</th><th className="text-right">Legs</th><th className="text-right">Avg</th><th className="text-right">F-Ø</th><th className="text-right">HF</th><th className="text-right">Pkte</th></tr>
            </thead>
            <tbody>
              {table.map((row) => <tr key={row.player_id} className="border-t dc-border">
                <td className="py-2 font-mono-data">{row.rank}.</td><td className="font-medium">{row.name}</td><td className="text-right font-mono-data">{row.played}</td><td className="text-right font-mono-data">{row.wins}</td><td className="text-right font-mono-data">{row.losses}</td><td className="text-right font-mono-data">{row.legs_for}:{row.legs_against}</td><td className="text-right font-mono-data">{row.average || "—"}</td><td className="text-right font-mono-data">{row.finish_average || "—"}</td><td className="text-right font-mono-data">{row.high_finish || "—"}</td><td className="text-right font-mono-data font-bold">{row.points}</td>
              </tr>)}
            </tbody>
          </table>
        </section>

        <section className="dc-border p-6 report-section">
          <h2 className="font-display text-2xl mb-4">Ergebnisse</h2>
          {matches.length === 0 ? <p className="print-muted" style={{ color: "var(--dc-muted)" }}>Für diesen Spieltag wurde noch kein Spielplan erzeugt.</p> : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase print-muted" style={{ color: "var(--dc-muted)" }}><tr><th className="text-left py-2">Partie / Ergebnis</th><th className="text-left">Highlights</th></tr></thead>
              <tbody>
                {matches.map((match) => {
                  const p1 = players[match.player1_id]?.name || "?";
                  const p2 = players[match.player2_id]?.name || "?";
                  const highlights = [];
                  if (match.match_high_finish_p1) highlights.push(`HF ${p1}: ${match.match_high_finish_p1}`);
                  if (match.match_high_finish_p2) highlights.push(`HF ${p2}: ${match.match_high_finish_p2}`);
                  if (match.match_s180_p1) highlights.push(`${p1}: ${match.match_s180_p1}× 180`);
                  if (match.match_s180_p2) highlights.push(`${p2}: ${match.match_s180_p2}× 180`);
                  return <tr key={match.id} className="border-t dc-border">
                    <td className="py-3 whitespace-nowrap"><span className={match.winner_id === match.player1_id ? "font-bold" : ""}>{p1}</span><span className="font-mono-data text-base mx-2">{match.played ? `${match.legs_p1}:${match.legs_p2}` : "-:-"}</span><span className={match.winner_id === match.player2_id ? "font-bold" : ""}>{p2}</span></td>
                    <td className="print-muted text-xs" style={{ color: "var(--dc-muted)" }}>{highlights.join(" · ")}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </>
  );
}
